"use client";

import { useEffect } from "react";
import { useLogEvent } from "~/hooks/useLogEvent";
import {
    attemptChunkErrorRecovery,
    isChunkLoadError,
} from "~/utils/chunkErrorRecovery";
import {
    allowlistedErrorName,
    redactPersonalData,
} from "~/utils/errorTelemetry";
import { getBrowserPathname } from "~/utils/browserHostname";

export const RENDER_CRASH_TAG = "render-crash";

/**
 * Reports a crash caught by an `error.tsx` / `global-error.tsx` boundary, then
 * runs chunk recovery.
 *
 * Both boundaries used to do the recovery and nothing else, which left a whole
 * class of failures with no record anywhere. React routes an error caught by a
 * boundary through `onCaughtError`, not `reportError`, so the `window.onerror`
 * handler in `DataDogLogs.tsx` never sees it. A checkout page that died on
 * render — a price the API returned in an unexpected shape, an `undefined` in
 * the summary, a broken item in the cart — showed "Something went wrong", the
 * customer left, and nothing distinguished it from an ordinary abandon.
 *
 * A stale bundle is reported at `warn` rather than `error`: it means the
 * customer is holding a page from a previous deploy and asking for a hashed
 * chunk that no longer exists. That happens after every release, heals itself
 * through the reload below, and is nobody's defect.
 *
 * One limitation worth knowing when reading these logs: `DataDogLogs` mounts in
 * the root layout, so a crash *in that layout* reaches `global-error.tsx` with
 * the logs SDK never initialised, and the report is lost. Everything below the
 * layout — which is all of checkout — reports normally.
 */
export function useReportBoundaryCrash(
    error: Error & { digest?: string }
): void {
    const logEvent = useLogEvent();

    useEffect(() => {
        const isStaleBundle = isChunkLoadError(error);

        logEvent({
            message: "render_crash",
            level: isStaleBundle ? "warn" : "error",
            tag: RENDER_CRASH_TAG,
            data: {
                pathname: getBrowserPathname(),
                isStaleBundle,
                // Server-side crashes only: Next.js swaps the real message for
                // a generic one and leaves this hash to correlate against the
                // server log. A client render crash arrives without one.
                digest: error.digest,
                errorName: allowlistedErrorName(error),
                // Redacted, not dropped: React routes a caught error through
                // `onCaughtError`, so `window.onerror` files no copy, and
                // without a digest the rest of this payload is a pathname and
                // the word "TypeError". The stack needs the same pass because
                // V8 prefixes the raw message to the frames; the frames
                // themselves survive it.
                error: redactPersonalData(error.message),
                stack: redactPersonalData(error.stack),
            },
        });

        attemptChunkErrorRecovery(error);
    }, [error, logEvent]);
}
